import { createContext, useContext, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useToaster from "../../components/alert";
import Const from "../../utils/constant";
import { getCoursesList, deleteCourse, getCourseDetails } from "../../api/courses";

const PageContext = createContext();

export const PageProvider = ({ children }) => {
  const toaster = useToaster();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedCourseForDetails, setSelectedCourseForDetails] = useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCourseForDelete, setSelectedCourseForDelete] = useState(null);

  const getCoursesListQuery = useQuery({
    queryKey: ["coachCourses", currentPage, searchTerm],
    queryFn: () => getCoursesList({ queryKey: [{ page: currentPage, limit: 10, term: searchTerm }] }),
  });

  const selectedCourseId = selectedCourseForDetails?.id;
  const courseDetailsQuery = useQuery({
    queryKey: ["courseDetails", selectedCourseId],
    queryFn: () => getCourseDetails(selectedCourseId),
    enabled: Boolean(selectedCourseId && isDetailsModalOpen),
  });

  const deleteCourseMutation = useMutation({
    mutationFn: deleteCourse,
    onSuccess: async (res, deletedId) => {
      toaster.success(Const.MESSAGES.SUCCESS, "Course deleted successfully.");
      setIsDeleteModalOpen(false);
      setSelectedCourseForDelete(null);

      // Optimistically remove course from react-query cached list immediately
      queryClient.setQueriesData(
        { queryKey: ["coachCourses"] },
        (oldData) => {
          if (!oldData?.data?.data) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              data: oldData.data.data.filter((item) => item.id !== deletedId),
            },
          };
        }
      );

      await queryClient.invalidateQueries({ queryKey: ["coachCourses"] });
      await getCoursesListQuery.refetch();
    },
    onError: (err) => {
      toaster.error(
        Const.MESSAGES.FAILED,
        err?.response?.data?.message || err?.response?.data?.error || "Failed to delete course."
      );
    },
  });

  const handleOnChange = (e) => {
    const { value } = e.target;
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNo) => {
    setCurrentPage(pageNo);
  };

  const handleOpenDetailsModal = (course) => {
    setSelectedCourseForDetails(course);
    setIsDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedCourseForDetails(null);
  };

  const handleOpenDeleteModal = (course) => {
    setSelectedCourseForDelete(course);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedCourseForDelete(null);
  };

  const handleConfirmDelete = () => {
    if (selectedCourseForDelete?.id) {
      deleteCourseMutation.mutate(selectedCourseForDelete.id);
    }
  };

  const detailedCourse = courseDetailsQuery?.data?.data?.data?.[0] || selectedCourseForDetails;

  return (
    <PageContext.Provider
      value={{
        getCoursesListQuery,
        currentPage,
        handlePageChange,
        searchTerm,
        handleOnChange,
        isDetailsModalOpen,
        selectedCourseForDetails: detailedCourse,
        isCourseDetailsLoading: courseDetailsQuery.isPending,
        handleOpenDetailsModal,
        handleCloseDetailsModal,
        isDeleteModalOpen,
        selectedCourseForDelete,
        handleOpenDeleteModal,
        handleCloseDeleteModal,
        handleConfirmDelete,
        isDeleting: deleteCourseMutation.isPending,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const useCoachCoursesProvider = () => {
  return useContext(PageContext);
};
